import {
  HHBox,
  HHButton,
  HHGrid,
  HHSelect,
  HHSelectChangeEvent,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import { Rule } from '@mui/icons-material';
import { FormControlLabel, InputLabel, Switch, TextField } from '@mui/material';
import { useMemo } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { HCModal } from '../../hinge-connect/components/hinge-connect-modal';
import { useCreateRule } from '../../hinge-connect/rules/hooks/use-create-rule';
import { FILE_INGESTION_RESOURCE_TYPES } from '../constants/file-ingestion.constant';
import {
  FileType,
  FILE_TYPES,
  FILE_TYPE_DISPLAY_NAMES,
} from '../constants/file-types.constant';
import {
  DEFAULT_FORM_VALUES,
  ProviderFormData,
} from '../types/provider-form.types';
import { getFileTypeConfig } from '../utils/file-type-config';
import { providerFormSchema } from '../validation/provider-form-schema';

const LINE_OF_BUSINESS_OPTIONS = [
  'Federal',
  'Self-pay',
  'VA Workers Comp',
  'Medicare Medicade',
  'Medicare Advantage',
  'Fully Insured',
  'ASO',
];

// Define types for generic metadata
type Cadence = {
  timeframe: string;
  day?: string;
};

type GenericMetadata = {
  permittedUse?: string;
  contentType?: string;
  clientIdentifier?: string;
  clientColumn?: string;
  insurerIdentifier?: string;
  lineOfBusiness?: string;
  cadence?: Cadence;
};

const FILE_TYPE_OPTIONS: Array<{ value: FileType; displayName: string }> = [
  {
    value: FILE_TYPES.SINGLE_CLIENT_SINGLE_INSURER,
    displayName: FILE_TYPE_DISPLAY_NAMES.SINGLE_CLIENT_SINGLE_INSURER,
  },
  {
    value: FILE_TYPES.SINGLE_CLIENT_MULTI_INSURER,
    displayName: FILE_TYPE_DISPLAY_NAMES.SINGLE_CLIENT_MULTI_INSURER,
  },
  {
    value: FILE_TYPES.MULTI_CLIENT_SINGLE_INSURER,
    displayName: FILE_TYPE_DISPLAY_NAMES.MULTI_CLIENT_SINGLE_INSURER,
  },
  {
    value: FILE_TYPES.MULTI_CLIENT_MULTI_INSURER,
    displayName: FILE_TYPE_DISPLAY_NAMES.MULTI_CLIENT_MULTI_INSURER,
  },
];

const CONTENT_TYPE_OPTIONS = [
  { value: 'medical_claims', displayName: 'Medical Claims' },
  { value: 'pharmacy_claims', displayName: 'Pharmacy Claims' },
  { value: 'medical_pharmacy_claims', displayName: 'Medical Pharmacy Claims' },
  { value: 'eligibility', displayName: 'Eligibility' },
];

const PERMITTED_USE_OPTIONS = [
  { value: 'TE', displayName: 'TE' },
  { value: 'ROI', displayName: 'ROI' },
  { value: 'Both', displayName: 'Both' },
];

const CADENCE_TIMEFRAME_OPTIONS = [
  { value: 'Quarterly', displayName: 'Quarterly' },
  { value: 'Monthly', displayName: 'Monthly' },
  { value: 'Bi-Weekly', displayName: 'Bi-Weekly' },
  { value: 'Weekly', displayName: 'Weekly' },
  { value: 'Daily', displayName: 'Daily' },
  { value: 'One Time', displayName: 'One Time' },
];

const CADENCE_DAY_OPTIONS = [
  { value: 'Monday', displayName: 'Monday' },
  { value: 'Tuesday', displayName: 'Tuesday' },
  { value: 'Wednesday', displayName: 'Wednesday' },
  { value: 'Thursday', displayName: 'Thursday' },
  { value: 'Friday', displayName: 'Friday' },
  { value: 'Saturday', displayName: 'Saturday' },
  { value: 'Sunday', displayName: 'Sunday' },
];

const FILE_FORMAT_OPTIONS = [
  { value: 'csv', displayName: 'CSV (Comma Separated Values)' },
  { value: 'fixed_width_txt', displayName: 'Fixed Width' },
];

const FIELD_SEPARATOR_OPTIONS = [
  { value: ',', displayName: 'Comma (,)' },
  { value: '\t', displayName: 'Tab (\\t)' },
  { value: ' ', displayName: 'Space ( )' },
  { value: ';', displayName: 'Semicolon (;)' },
  { value: '|', displayName: 'Pipe (|)' },
  { value: ':', displayName: 'Colon (:)' },
  { value: '~', displayName: 'Tilde (~)' },
  { value: '^', displayName: 'Caret (^)' },
  { value: '#', displayName: 'Hash (#)' },
  { value: '@', displayName: 'At (@)' },
];

type NewProviderFormProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: (providerName: string) => void;
};

// FileIdentificationSection using useFormContext
const FileIdentificationSection = (): JSX.Element => {
  const { control, watch, setValue } = useFormContext<ProviderFormData>();

  const fileType = watch('fileType');
  const cadenceTimeframe = watch('cadenceTimeframe');
  const lineOfBusiness = watch('lineOfBusiness');

  const config = getFileTypeConfig(fileType);

  const lineOfBusinessLabel = useMemo(() => {
    if (!lineOfBusiness || lineOfBusiness.length === 0) {
      return 'Select Line of Business';
    }
    if (lineOfBusiness.length === 1) {
      return lineOfBusiness[0];
    }
    if (lineOfBusiness.length <= 3) {
      return lineOfBusiness.join(', ');
    }
    return `${lineOfBusiness.length} lines of business selected`;
  }, [lineOfBusiness]);

  const lineOfBusinessSelectOptions = useMemo(
    () =>
      LINE_OF_BUSINESS_OPTIONS.map(option => ({
        displayName: lineOfBusiness?.includes(option) ? `${option} ✔` : option,
        value: option,
      })),
    [lineOfBusiness],
  );

  const handleLineOfBusinessChange = (e: HHSelectChangeEvent): void => {
    const selectedValue = e.target.value;
    if (
      !selectedValue ||
      typeof selectedValue !== 'string' ||
      selectedValue.trim() === ''
    ) {
      return;
    }

    const trimmedValue = selectedValue.trim();
    const currentSelection = lineOfBusiness;

    if (currentSelection.includes(trimmedValue)) {
      setValue(
        'lineOfBusiness',
        currentSelection.filter(item => item !== trimmedValue),
        { shouldValidate: true },
      );
    } else {
      setValue('lineOfBusiness', [...currentSelection, trimmedValue], {
        shouldValidate: true,
      });
    }
  };

  return (
    <HHBox>
      <HHTypography
        hhVariant="variant-bypass"
        sx={{
          fontWeight: 500,
          marginBottom: 2,
          fontSize: '16px',
        }}
      >
        File Identification
      </HHTypography>
      <HHGrid container spacing={3}>
        <HHGrid item xs={6}>
          <Controller
            control={control}
            name="configName"
            render={({ field, fieldState: { error } }): JSX.Element => (
              <TextField
                {...field}
                label="Config Name *"
                placeholder="e.g., medical-claims-config"
                fullWidth
                size="small"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </HHGrid>
        <HHGrid item xs={6}>
          <Controller
            control={control}
            name="providerName"
            render={({ field, fieldState: { error } }): JSX.Element => (
              <TextField
                {...field}
                label="Provider Name *"
                placeholder="e.g., BCBS_TN, CAMBIA"
                fullWidth
                size="small"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </HHGrid>
      </HHGrid>

      {/* File Name Regex Pattern row */}
      <HHGrid container spacing={3} sx={{ marginTop: 1 }}>
        <HHGrid item xs={12}>
          <Controller
            control={control}
            name="fileNamePattern"
            render={({ field, fieldState: { error } }): JSX.Element => (
              <TextField
                {...field}
                label="File Name Regex Pattern *"
                placeholder=".*\.csv$ or claims_.*\.txt"
                fullWidth
                size="small"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </HHGrid>
      </HHGrid>

      {/* File Type row */}
      <HHGrid container spacing={3} sx={{ marginTop: 1 }}>
        <HHGrid item xs={6}>
          <Controller
            control={control}
            name="fileType"
            render={({ field }): JSX.Element => (
              <HHSelect
                label="File Type *"
                selectOptions={FILE_TYPE_OPTIONS}
                initialSelectValue={field.value}
                onChange={(e: HHSelectChangeEvent): void => {
                  field.onChange(e.target.value as FileType);
                  // Clear related fields when file type changes
                  setValue('clientColumn', '', { shouldValidate: true });
                  setValue('clientIdentifier', '', { shouldValidate: true });
                  setValue('insurerIdentifier', '', { shouldValidate: true });
                }}
                width={'100%'}
                size="small"
                id="file-type-select"
              />
            )}
          />
        </HHGrid>
        {config?.showClientColumn && (
          <HHGrid item xs={6}>
            <Controller
              control={control}
              name="clientColumn"
              render={({ field, fieldState: { error } }): JSX.Element => (
                <TextField
                  {...field}
                  label="Client Column *"
                  placeholder="e.g., client_id, client_name"
                  fullWidth
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </HHGrid>
        )}
        {config?.showClientIdentifier && (
          <HHGrid item xs={6}>
            <Controller
              control={control}
              name="clientIdentifier"
              render={({ field, fieldState: { error } }): JSX.Element => (
                <TextField
                  {...field}
                  label="Client Identifier *"
                  placeholder="The admin panel client identifier"
                  fullWidth
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </HHGrid>
        )}
      </HHGrid>

      {/* Additional identification fields */}
      <HHGrid container spacing={3} sx={{ marginTop: 1 }}>
        <HHGrid item xs={6}>
          <HHSelect
            label="Line of Business *"
            selectOptions={lineOfBusinessSelectOptions}
            onChange={handleLineOfBusinessChange}
            renderValue={(): string => lineOfBusinessLabel}
            width={'100%'}
            id="line-of-business-select"
            aria-label="Line of Business multi-select field. Click to select multiple business lines from the available options"
            aria-describedby="line-of-business-description"
          />
          <div
            id="line-of-business-description"
            style={{ display: 'none' }}
            aria-hidden="true"
          >
            Multi-select dropdown for line of business. Use arrow keys to
            navigate options and space or enter to select. Multiple selections
            allowed.
          </div>
        </HHGrid>
        <HHGrid item xs={6}>
          <Controller
            control={control}
            name="insurerIdentifier"
            render={({ field, fieldState: { error } }): JSX.Element => (
              <TextField
                {...field}
                label={config?.insurerIdentifierLabel ?? 'Insurer Identifier'}
                value={config?.insurerIdentifierValue ?? field.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                  field.onChange(e.target.value)
                }
                placeholder={config?.insurerIdentifierPlaceholder ?? ''}
                disabled={config?.insurerIdentifierDisabled ?? false}
                fullWidth
                size="small"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </HHGrid>
      </HHGrid>

      {/* Content Type and Permitted Use row */}
      <HHGrid container spacing={3} sx={{ marginTop: 1 }}>
        <HHGrid item xs={6}>
          <Controller
            control={control}
            name="contentType"
            render={({ field }): JSX.Element => (
              <HHSelect
                label="Content Type *"
                selectOptions={CONTENT_TYPE_OPTIONS}
                initialSelectValue={field.value}
                onChange={(e: HHSelectChangeEvent): void =>
                  field.onChange(e.target.value)
                }
                width={'100%'}
                size="small"
                id="content-type-select"
              />
            )}
          />
        </HHGrid>
        <HHGrid item xs={6}>
          <Controller
            control={control}
            name="permittedUse"
            render={({ field }): JSX.Element => (
              <HHSelect
                label="Permitted Use *"
                selectOptions={[
                  { value: '', displayName: 'Select...' },
                  ...PERMITTED_USE_OPTIONS,
                ]}
                initialSelectValue={field.value}
                onChange={(e: HHSelectChangeEvent): void =>
                  field.onChange(e.target.value)
                }
                width={'100%'}
                size="small"
                id="permitted-use-select"
              />
            )}
          />
        </HHGrid>
      </HHGrid>

      {/* Cadence Timeframe row */}
      <HHGrid container spacing={3} sx={{ marginTop: 1 }}>
        <HHGrid item xs={6}>
          <Controller
            control={control}
            name="cadenceTimeframe"
            render={({ field }): JSX.Element => (
              <HHSelect
                label="Cadence Timeframe"
                selectOptions={CADENCE_TIMEFRAME_OPTIONS}
                initialSelectValue={field.value}
                onChange={(e: HHSelectChangeEvent): void => {
                  const value = e.target.value as string;
                  field.onChange(value);
                  // Clear day if timeframe doesn't support it
                  if (!['Bi-Weekly', 'Weekly'].includes(value)) {
                    setValue('cadenceDay', '', { shouldValidate: true });
                  }
                }}
                width={'100%'}
                size="small"
                id="cadence-timeframe-select"
              />
            )}
          />
        </HHGrid>
        {(cadenceTimeframe === 'Bi-Weekly' ||
          cadenceTimeframe === 'Weekly') && (
          <HHGrid item xs={6}>
            <Controller
              control={control}
              name="cadenceDay"
              render={({ field }): JSX.Element => (
                <HHSelect
                  label="Cadence Day"
                  selectOptions={CADENCE_DAY_OPTIONS}
                  initialSelectValue={field.value}
                  onChange={(e: HHSelectChangeEvent): void =>
                    field.onChange(e.target.value)
                  }
                  width={'100%'}
                  size="small"
                  id="cadence-day-select"
                />
              )}
            />
          </HHGrid>
        )}
      </HHGrid>
    </HHBox>
  );
};

// FileProcessingSection using useFormContext
const FileProcessingSection = (): JSX.Element => {
  const { control, watch, trigger } = useFormContext<ProviderFormData>();

  const fileFormat = watch('fileFormat');

  const fixedWidthView = useMemo(
    () => fileFormat === 'fixed_width_txt',
    [fileFormat],
  );

  return (
    <HHBox>
      <HHTypography
        hhVariant="variant-bypass"
        sx={{
          fontWeight: 500,
          marginBottom: 2,
          fontSize: '16px',
        }}
      >
        File Processing
      </HHTypography>
      <HHGrid container spacing={3}>
        <HHGrid item xs={fixedWidthView ? 12 : 6}>
          <Controller
            control={control}
            name="fileFormat"
            render={({ field }): JSX.Element => (
              <HHSelect
                label="File Format"
                selectOptions={FILE_FORMAT_OPTIONS}
                initialSelectValue={field.value}
                onChange={(e: HHSelectChangeEvent): void => {
                  field.onChange(e.target.value);
                  // Re-validate fixedWidthConfig when format changes
                  trigger('fixedWidthConfig');
                }}
                width={'100%'}
                size="small"
                id="file-format-select"
              />
            )}
          />
        </HHGrid>
        {!fixedWidthView && (
          <HHGrid item xs={6}>
            <Controller
              control={control}
              name="separator"
              render={({ field }): JSX.Element => (
                <HHSelect
                  label="Field Separator"
                  selectOptions={FIELD_SEPARATOR_OPTIONS}
                  initialSelectValue={field.value}
                  onChange={(e: HHSelectChangeEvent): void =>
                    field.onChange(e.target.value)
                  }
                  width={'100%'}
                  size="small"
                  id="field-separator-select"
                />
              )}
            />
          </HHGrid>
        )}
      </HHGrid>

      {/* File Header Toggle row - only for CSV */}
      {!fixedWidthView && (
        <HHGrid container spacing={3} sx={{ marginTop: 1 }} alignItems="center">
          <HHGrid item xs={6}>
            <InputLabel
              sx={{
                fontSize: '12px',
                color: 'rgba(0, 0, 0, 0.6)',
                fontWeight: 400,
              }}
            >
              Header Configuration
            </InputLabel>
          </HHGrid>
          <HHGrid item xs={6}>
            <Controller
              control={control}
              name="hasHeader"
              render={({ field }): JSX.Element => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(
                        e: React.ChangeEvent<HTMLInputElement>,
                      ): void => field.onChange(e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <HHTypography hhVariant="body2" sx={{ fontSize: '14px' }}>
                      File has header row
                    </HHTypography>
                  }
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '14px',
                    },
                  }}
                />
              )}
            />
          </HHGrid>
        </HHGrid>
      )}
    </HHBox>
  );
};

// ErrorMessagesSection
type ErrorMessagesSectionProps = {
  errorMessages: string | null;
};

const ErrorMessagesSection = ({
  errorMessages,
}: ErrorMessagesSectionProps): JSX.Element | null => {
  if (!errorMessages) return null;

  return (
    <HHBox
      sx={{
        backgroundColor: '#FFEBEE',
        border: '1px solid #FFCDD2',
        borderRadius: '8px',
        padding: '12px',
        marginTop: 2,
      }}
    >
      <HHTypography
        hhVariant="variant-bypass"
        sx={{ color: '#D32F2F', fontSize: '14px' }}
      >
        {errorMessages}
      </HHTypography>
    </HHBox>
  );
};

// Helper function to build generic metadata
const buildGenericMetadata = (formData: ProviderFormData): GenericMetadata => {
  const metadata: GenericMetadata = {};
  const config = getFileTypeConfig(formData.fileType);

  // Always include these if provided
  if (formData.permittedUse) metadata.permittedUse = formData.permittedUse;
  if (formData.contentType) metadata.contentType = formData.contentType;

  // Get client and insurer identifiers from config
  const identifiers = config.getMetadata({
    clientIdentifier: formData.clientIdentifier,
    clientColumn: formData.clientColumn,
    insurerIdentifier: formData.insurerIdentifier,
  });

  Object.assign(metadata, identifiers);

  // Line of business
  if (formData.lineOfBusiness.length > 0) {
    metadata.lineOfBusiness = formData.lineOfBusiness.join(', ');
  }

  // Cadence
  if (formData.cadenceTimeframe) {
    metadata.cadence = {
      timeframe: formData.cadenceTimeframe,
      ...(formData.cadenceDay && { day: formData.cadenceDay }),
    };
  }

  return metadata;
};

export const NewProviderForm = ({
  open,
  onClose,
  onSuccess,
}: NewProviderFormProps): JSX.Element => {
  const methods = useForm<ProviderFormData>({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: yupResolver<ProviderFormData>(providerFormSchema),
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = methods;

  const { createRule, loading, errorMessages } = useCreateRule({
    disableAutoNavigation: true,
  });

  const fileFormat = watch('fileFormat');
  const fixedWidthConfig = watch('fixedWidthConfig');

  const fixedWidthView = useMemo(
    () => fileFormat === 'fixed_width_txt',
    [fileFormat],
  );

  const onSubmit = async (formData: ProviderFormData): Promise<void> => {
    try {
      // Build parser config based on file format
      const parserConfig = fixedWidthView
        ? {
            line_sep: '\n',
            columns_spec: JSON.parse(formData.fixedWidthConfig),
          }
        : {
            sep: formData.separator,
            header: formData.hasHeader,
          };

      await createRule({
        variables: {
          createRuleInput: {
            source: formData.providerName.trim(),
            resourceTypes: [...FILE_INGESTION_RESOURCE_TYPES],
            definition: {
              name: formData.configName.trim(),
              operator: 'all',
              criteria: [
                {
                  key: 'fileName',
                  name: 'file-name',
                  value: formData.fileNamePattern.trim(),
                  operator: 'matches',
                },
                {
                  key: 'dataProvider',
                  name: 'data-provider',
                  value: formData.providerName.trim(),
                  operator: 'contains',
                },
              ],
            },
            actions: [
              {
                type: 'FILE_INGESTION_MESSAGE_BUILDER',
                metadata: {
                  parser: {
                    config: parserConfig,
                    fileFormat: formData.fileFormat,
                  },
                  fileType: formData.fileType,
                  genericMetadata: buildGenericMetadata(formData),
                },
                actions: [
                  {
                    type: 'KAFKA_PUBLISH',
                  },
                ],
              },
            ],
          },
        },
      });

      // Reset form after successful submission
      reset();
      onSuccess(formData.providerName.trim());
    } catch (error) {
      console.error('Failed to create rule:', error);
    }
  };

  // Check if fixedWidthConfig is valid when it's required
  const isFixedWidthConfigValid = useMemo(() => {
    if (!fixedWidthView) return true;
    if (!fixedWidthConfig.trim()) return false;

    try {
      const parsed = JSON.parse(fixedWidthConfig);
      if (!Array.isArray(parsed)) return false;

      return parsed.every(
        item =>
          typeof item === 'object' &&
          item !== null &&
          'name' in item &&
          'start_pos' in item &&
          'length' in item,
      );
    } catch {
      return false;
    }
  }, [fixedWidthView, fixedWidthConfig]);

  const modalContent = (
    <HHBox sx={{ padding: '0 24px 24px 24px' }}>
      {/* Custom Title with Icon */}
      <HHBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: 3,
          paddingBottom: 2,
          borderBottom: '1px solid #E0E0E0',
        }}
      >
        <HHBox
          sx={{
            backgroundColor: '#1565C0',
            background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
            borderRadius: '6px',
            padding: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(21, 101, 192, 0.25)',
          }}
        >
          <Rule
            style={{
              fontSize: '18px',
              color: 'white',
            }}
          />
        </HHBox>
        <HHTypography
          hhVariant="h4"
          sx={{
            fontWeight: 600,
            color: '#1565C0',
            margin: 0,
          }}
        >
          Create New File Configuration
        </HHTypography>
      </HHBox>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <HHBox
            sx={{
              maxHeight: fixedWidthView ? '75vh' : '60vh',
              overflowY: 'auto',
            }}
          >
            {fixedWidthView ? (
              <HHGrid container spacing={4}>
                {/* Left Column: Form Fields */}
                <HHGrid item xs={12} md={6}>
                  <HHStack direction="column" spacing={4}>
                    <FileIdentificationSection />
                    <FileProcessingSection />
                    <ErrorMessagesSection errorMessages={errorMessages} />
                  </HHStack>
                </HHGrid>

                {/* Right Column: fixed_width_txt Configuration */}
                <HHGrid item xs={12} md={6}>
                  <HHBox
                    sx={{
                      position: 'sticky',
                      top: 24,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      paddingTop: 2,
                    }}
                  >
                    <Controller
                      control={control}
                      name="fixedWidthConfig"
                      render={({
                        field,
                        fieldState: { error },
                      }): JSX.Element => (
                        <>
                          <TextField
                            {...field}
                            label="fixed_width_txt Configuration (JSON) *"
                            placeholder='[{"name": "field1", "start_pos": 0, "length": 10}, {"name": "field2", "start_pos": 10, "length": 5}]'
                            fullWidth
                            multiline
                            minRows={25}
                            maxRows={35}
                            size="small"
                            error={!!error}
                            inputProps={{
                              role: 'textbox',
                              name: 'fixed-width-config',
                            }}
                            sx={{
                              '& .MuiInputLabel-root': {
                                backgroundColor: 'white',
                                paddingX: 0.5,
                              },
                              '& .MuiOutlinedInput-root': {
                                alignItems: 'flex-start',
                                '& fieldset': {
                                  borderColor: error
                                    ? '#F44336'
                                    : isFixedWidthConfigValid &&
                                        field.value.trim()
                                      ? '#66BB6A'
                                      : 'rgba(0, 0, 0, 0.23)',
                                  borderWidth: error ? '2px' : '1px',
                                },
                                '&:hover fieldset': {
                                  borderColor: error
                                    ? '#F44336'
                                    : 'rgba(0, 0, 0, 0.87)',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: error
                                    ? '#EF5350'
                                    : isFixedWidthConfigValid &&
                                        field.value.trim()
                                      ? '#66BB6A'
                                      : '#1976d2',
                                  borderWidth: '2px',
                                },
                              },
                            }}
                          />
                          {error && (
                            <HHTypography
                              hhVariant="body2"
                              sx={{
                                color: '#F44336',
                                fontSize: '12px',
                                marginLeft: 1.5,
                              }}
                            >
                              {error.message}
                            </HHTypography>
                          )}
                        </>
                      )}
                    />
                  </HHBox>
                </HHGrid>
              </HHGrid>
            ) : (
              <HHStack direction="column" spacing={4}>
                <FileIdentificationSection />
                <FileProcessingSection />
                <ErrorMessagesSection errorMessages={errorMessages} />
              </HHStack>
            )}
          </HHBox>

          {/* Form Buttons */}
          <HHBox
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              paddingTop: 3,
              borderTop: '1px solid #E0E0E0',
              marginTop: 3,
              marginLeft: 3,
            }}
          >
            <HHButton
              hhVariant="outlined"
              onClick={onClose}
              sx={{
                borderColor: '#1565C0',
                color: '#1565C0',
                fontWeight: 600,
                textTransform: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: '#E3F2FD',
                  borderColor: '#0D47A1',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Cancel
            </HHButton>
            <HHButton
              hhVariant="contained"
              type="submit"
              disabled={!isValid || loading}
              onClick={(e): void => {
                e.preventDefault();
                handleSubmit(onSubmit)();
              }}
              sx={{
                backgroundColor: '#1565C0',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(21, 101, 192, 0.3)',
                '&:hover': {
                  backgroundColor: '#0D47A1',
                  boxShadow: '0 6px 16px rgba(21, 101, 192, 0.4)',
                },
                '&:disabled': {
                  backgroundColor: '#BDBDBD',
                  color: '#9E9E9E',
                  boxShadow: 'none',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {loading ? 'Creating...' : 'Create File Configuration'}
            </HHButton>
          </HHBox>
        </form>
      </FormProvider>
    </HHBox>
  );

  return (
    <HCModal
      title=""
      isOpen={open}
      handleClose={onClose}
      component={modalContent}
      maxWidth={fixedWidthView ? 'lg' : 'sm'}
      fullWidth
    />
  );
};
